import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Single source of truth for shop details on the warranty card.
const SHOP = {
  name: 'Ashop Rotterdam',
  tagline: 'Phones · Money Transfer · Print Services',
  address: 'Schiedamseweg, Rotterdam',
  phone: '+31 6 14359710',
  email: 'shabazbaig786@gmail.com',
};

// Brands we sell — logos rendered from Simple Icons CDN (real brand marks)
const BRANDS = [
  { name: 'Samsung', src: 'https://cdn.simpleicons.org/samsung/1428A0', label: 'Samsung' },
  { name: 'Xiaomi', src: 'https://cdn.simpleicons.org/xiaomi/FF6900', label: 'Mi · Xiaomi' },
  // Redmi is a Xiaomi sub-brand — Simple Icons doesn't have a Redmi mark, so we render a clean wordmark
  { name: 'Redmi', wordmark: 'Redmi', label: 'Redmi' },
];

// Format today's date as "12 Feb 2026" for the default date field
const todayStr = () => {
  const d = new Date();
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Reusable filled-line display for the on-screen preview
const FilledLine = ({ value, dotted = true }) => (
  value && value.trim()
    ? <span className="font-bold text-slate-900 text-[15px] leading-none">{value}</span>
    : (dotted
        ? <span className="w-full inline-block border-b-2 border-dotted border-slate-300"></span>
        : null)
);

const Warranty = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customerName: '',
    mobilePrice: '',
    modelImei: '',
    simLock: '',
    dateOfPurchase: todayStr(),
  });

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const clearAll = () => setForm({
    customerName: '',
    mobilePrice: '',
    modelImei: '',
    simLock: '',
    dateOfPurchase: todayStr(),
  });

  const escape = (v) => String(v || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));

  // Small helper — show filled value (bold), else a dotted underline placeholder for the print
  const printValue = (v) => v && v.trim() ? `<span class="filled">${escape(v)}</span>` : `<span class="empty"></span>`;

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) {
      alert('Please allow pop-ups to print the warranty card.');
      return;
    }
    const brandsHtml = BRANDS.map((b) => {
      if (b.src) {
        return `<div class="brand"><img src="${b.src}" alt="${b.name}" /><span>${b.label}</span></div>`;
      }
      return `<div class="brand"><span class="wordmark">${b.wordmark}</span><span>${b.label}</span></div>`;
    }).join('');

    // Customer line only shown if provided
    const customerRow = form.customerName.trim()
      ? `<div class="field"><span class="lbl">Customer Name</span><span class="line">${printValue(form.customerName)}</span></div>`
      : '';

    w.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Warranty Card · ${SHOP.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #1f2937; background: #fff; }
            .card { max-width: 780px; margin: 0 auto; border: 3px solid #1e3a8a; border-radius: 14px; overflow: hidden; }
            .header { background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%); color: #fff; padding: 20px 26px; display: flex; justify-content: space-between; align-items: center; }
            .shop-name { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
            .shop-sub { font-size: 11px; opacity: 0.85; margin-top: 2px; }
            .shop-contact { font-size: 11px; opacity: 0.8; margin-top: 6px; line-height: 1.5; }
            .stamp { text-align: right; }
            .stamp .badge { display: inline-block; background: #fbbf24; color: #78350f; padding: 8px 14px; border-radius: 8px; font-weight: 900; font-size: 13px; letter-spacing: 2px; text-transform: uppercase; border: 2px solid #fef3c7; }
            .stamp .years { font-size: 34px; font-weight: 900; line-height: 1; margin-top: 6px; }
            .stamp .years-lbl { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; opacity: 0.85; margin-top: 4px; }

            .title-row { background: #f8fafc; padding: 14px 26px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
            .title-row h1 { font-size: 18px; font-weight: 800; color: #1e3a8a; letter-spacing: -0.01em; }
            .title-row .cert { font-family: 'Menlo', monospace; font-size: 11px; color: #64748b; }

            .fields { padding: 22px 26px; display: grid; grid-template-columns: 1fr; gap: 14px; }
            .field { display: flex; align-items: flex-end; gap: 10px; }
            .field .lbl { font-size: 12px; font-weight: 700; color: #1e3a8a; min-width: 155px; text-transform: uppercase; letter-spacing: 0.8px; }
            .field .line { flex: 1; border-bottom: 2px solid #94a3b8; min-height: 28px; padding: 3px 4px 4px; display: flex; align-items: flex-end; }
            .field .line .filled { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: 0.2px; }
            .field .line .empty { display: inline-block; width: 100%; border-bottom: 2px dotted #cbd5e1; }

            .date-stamp { padding: 8px 26px 20px; display: grid; grid-template-columns: 1.2fr 1fr; gap: 20px; }
            .date-stamp .cell .lbl { font-size: 12px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px; }
            .date-stamp .cell .line { border-bottom: 2px solid #94a3b8; min-height: 34px; display: flex; align-items: flex-end; padding: 3px 4px 4px; }
            .date-stamp .cell .line .filled { font-size: 15px; font-weight: 700; color: #0f172a; }
            .date-stamp .cell.stampcell .box { border: 2px dashed #94a3b8; border-radius: 8px; min-height: 66px; display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 10px; text-transform: uppercase; letter-spacing: 3px; font-weight: 700; }

            .terms { background: #fef2f2; border-top: 1px solid #fecaca; padding: 16px 26px; }
            .terms h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 1.5px; color: #b91c1c; font-weight: 800; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
            .terms ul { padding-left: 18px; }
            .terms li { font-size: 11px; line-height: 1.7; color: #374151; margin-bottom: 3px; }
            .terms li strong { color: #b91c1c; }

            .brands { padding: 16px 26px; background: #f1f5f9; border-top: 1px solid #cbd5e1; display: flex; justify-content: center; gap: 40px; align-items: center; }
            .brand { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 6px; }
            .brand img { height: 32px; object-fit: contain; }
            .brand .wordmark { font-size: 22px; font-weight: 800; color: #FF6900; font-style: italic; letter-spacing: -0.02em; }
            .brand span:last-child { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }

            .footer { padding: 10px 26px; text-align: center; background: #1e3a8a; color: #dbeafe; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; }

            @media print {
              body { padding: 0; }
              .card { border-radius: 0; border: 2px solid #1e3a8a; }
            }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header">
              <div>
                <div class="shop-name">${SHOP.name}</div>
                <div class="shop-sub">${SHOP.tagline}</div>
                <div class="shop-contact">
                  ${SHOP.address}<br />
                  ${SHOP.phone} &nbsp;·&nbsp; ${SHOP.email}
                </div>
              </div>
              <div class="stamp">
                <span class="badge">Warranty</span>
                <div class="years">1</div>
                <div class="years-lbl">Year Cover</div>
              </div>
            </div>

            <div class="title-row">
              <h1>Mobile Phone Warranty Card</h1>
              <span class="cert">Certificate</span>
            </div>

            <div class="fields">
              ${customerRow}
              <div class="field"><span class="lbl">Mobile Price</span><span class="line">${printValue(form.mobilePrice)}</span></div>
              <div class="field"><span class="lbl">Model &amp; IMEI</span><span class="line">${printValue(form.modelImei)}</span></div>
              <div class="field"><span class="lbl">SIM Lock</span><span class="line">${printValue(form.simLock)}</span></div>
            </div>

            <div class="date-stamp">
              <div class="cell">
                <div class="lbl">Date of Purchase</div>
                <div class="line"><span class="filled">${escape(form.dateOfPurchase)}</span></div>
              </div>
              <div class="cell stampcell">
                <div class="lbl">Shop Stamp &amp; Signature</div>
                <div class="box">Stamp Here</div>
              </div>
            </div>

            <div class="terms">
              <h3>&#9888; Terms &amp; Conditions</h3>
              <ul>
                <li>This card provides a <strong>1-year limited warranty covering software issues only</strong> from the date of purchase.</li>
                <li><strong>Physical damage</strong> (cracks, dents, broken screen, bent frame) is <strong>NOT covered</strong> under any circumstances.</li>
                <li><strong>Liquid or water damage</strong> is <strong>NOT covered</strong>, regardless of exposure or cause.</li>
                <li>Damage from misuse, drops, unauthorized repairs, or third-party tampering <strong>voids this warranty</strong>.</li>
                <li>Battery, charger and accessories are <strong>not covered</strong> — these are consumables.</li>
                <li>${SHOP.name} is <strong>not liable</strong> for data loss, loss of use, business interruption or any indirect damages.</li>
                <li>Warranty is <strong>non-transferable</strong> and requires this card plus proof of purchase to be honored.</li>
                <li>All warranty decisions rest at the sole discretion of ${SHOP.name}.</li>
              </ul>
            </div>

            <div class="brands">${brandsHtml}</div>

            <div class="footer">Thank you for shopping with ${SHOP.name}</div>
          </div>
          <script>
            window.addEventListener('load', function() {
              setTimeout(function() { window.focus(); window.print(); }, 350);
            });
          </script>
        </body>
      </html>
    `);
    w.document.close();
  };

  const canPrint = form.mobilePrice.trim() && form.modelImei.trim() && form.simLock.trim() && form.dateOfPurchase.trim();

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4'}>
      <div className={embedded ? '' : 'max-w-6xl mx-auto'}>
        {/* Header actions */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          {!embedded && (
            <button
              onClick={() => navigate('/staff')}
              className="inline-flex items-center space-x-2 px-5 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200"
              data-testid="back-to-admin-btn"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
          )}
          <div className={`flex items-center gap-2 ${embedded ? 'ml-auto' : ''}`}>
            <button
              onClick={clearAll}
              className="inline-flex items-center space-x-2 px-4 py-3 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-sm border border-gray-200"
              data-testid="clear-form-btn"
            >
              <i className="fas fa-eraser"></i>
              <span>Clear</span>
            </button>
            <button
              onClick={handlePrint}
              disabled={!canPrint}
              className={`inline-flex items-center space-x-2 px-6 py-3 font-semibold rounded-xl transition-all shadow-lg ${canPrint ? 'bg-gradient-to-r from-blue-700 to-indigo-700 text-white hover:from-blue-800 hover:to-indigo-800 hover:shadow-xl' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              data-testid="print-warranty-btn"
              title={canPrint ? 'Print warranty card' : 'Fill in all fields to enable print'}
            >
              <i className="fas fa-print"></i>
              <span>Print Warranty Card</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* LEFT — Input form */}
          <div className="bg-white text-slate-900 rounded-2xl shadow-md border border-slate-200 p-5 h-fit sticky top-4" data-testid="warranty-form">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-900 mb-1">Fill in details</p>
            <h2 className="text-lg font-black text-slate-900 mb-4">Warranty details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Customer Name <span className="text-slate-300 normal-case tracking-normal">(optional)</span></label>
                <input
                  type="text"
                  value={form.customerName}
                  onChange={set('customerName')}
                  placeholder="e.g. Ali Khan"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  data-testid="input-customer"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Mobile Price *</label>
                <input
                  type="text"
                  value={form.mobilePrice}
                  onChange={set('mobilePrice')}
                  placeholder="e.g. € 350"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-sm font-mono"
                  data-testid="input-price"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Model &amp; IMEI *</label>
                <input
                  type="text"
                  value={form.modelImei}
                  onChange={set('modelImei')}
                  placeholder="e.g. Samsung A54 · 358240051111110"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-sm font-mono"
                  data-testid="input-imei"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">SIM Lock *</label>
                <input
                  type="text"
                  value={form.simLock}
                  onChange={set('simLock')}
                  placeholder="Unlocked / Locked to KPN / etc."
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  data-testid="input-sim"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Date of Purchase *</label>
                <input
                  type="text"
                  value={form.dateOfPurchase}
                  onChange={set('dateOfPurchase')}
                  placeholder={todayStr()}
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none text-sm font-mono"
                  data-testid="input-date"
                />
              </div>
              <p className="text-[11px] text-slate-400 pt-1 leading-relaxed">
                <i className="fas fa-info-circle mr-1"></i>
                The system fills all four values into the card. Only the <strong>stamp &amp; signature</strong> stays blank for you to sign on paper.
              </p>
            </div>
          </div>

          {/* RIGHT — Live preview of the printed card */}
          <div className="bg-white text-slate-900 rounded-2xl shadow-xl overflow-hidden border-4 border-blue-900" data-testid="warranty-preview">
            {/* Header */}
            <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 px-8 py-6 text-white flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-black tracking-tight">{SHOP.name}</h1>
                <p className="text-xs opacity-85 mt-0.5">{SHOP.tagline}</p>
                <p className="text-xs opacity-80 mt-2 leading-relaxed">
                  {SHOP.address}<br />
                  {SHOP.phone} · {SHOP.email}
                </p>
              </div>
              <div className="text-right">
                <span className="inline-block bg-amber-400 text-amber-900 px-3 py-1.5 rounded-lg font-black text-xs uppercase tracking-widest border-2 border-amber-200">
                  Warranty
                </span>
                <p className="text-5xl font-black leading-none mt-2">1</p>
                <p className="text-[10px] uppercase tracking-[0.25em] opacity-85 mt-1">Year Cover</p>
              </div>
            </div>

            {/* Title */}
            <div className="bg-slate-50 px-8 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-black text-blue-900 tracking-tight">Mobile Phone Warranty Card</h2>
              <span className="text-xs text-slate-500 font-mono">Certificate</span>
            </div>

            {/* Filled fields */}
            <div className="px-8 py-6 space-y-4">
              {form.customerName.trim() && (
                <div className="flex items-end gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-900 min-w-[160px]">Customer Name</span>
                  <div className="flex-1 border-b-2 border-solid border-slate-400 min-h-[30px] flex items-end px-1 pb-1">
                    <FilledLine value={form.customerName} />
                  </div>
                </div>
              )}
              {[
                { label: 'Mobile Price', value: form.mobilePrice },
                { label: 'Model & IMEI', value: form.modelImei },
                { label: 'SIM Lock', value: form.simLock },
              ].map((f) => (
                <div key={f.label} className="flex items-end gap-3" data-testid={`preview-${f.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-900 min-w-[160px]">{f.label}</span>
                  <div className="flex-1 border-b-2 border-solid border-slate-400 min-h-[30px] flex items-end px-1 pb-1">
                    <FilledLine value={f.value} />
                  </div>
                </div>
              ))}
            </div>

            {/* Date & Stamp */}
            <div className="px-8 pb-6 grid grid-cols-1 sm:grid-cols-[1.2fr_1fr] gap-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-1">Date of Purchase</p>
                <div className="border-b-2 border-solid border-slate-400 min-h-[34px] flex items-end px-1 pb-1">
                  <FilledLine value={form.dateOfPurchase} />
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-1">Shop Stamp &amp; Signature</p>
                <div className="border-2 border-dashed border-slate-300 rounded-lg h-16 flex items-center justify-center text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold">
                  Stamp Here
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="bg-red-50 border-t border-red-200 px-8 py-4">
              <p className="text-xs font-black uppercase tracking-wider text-red-700 mb-2 flex items-center gap-2">
                <i className="fas fa-triangle-exclamation"></i> Terms &amp; Conditions
              </p>
              <ul className="list-disc pl-5 space-y-1 text-[11px] text-slate-700 leading-relaxed">
                <li>This card provides a <strong className="text-red-700">1-year limited warranty covering software issues only</strong> from the date of purchase.</li>
                <li><strong className="text-red-700">Physical damage</strong> (cracks, dents, broken screen, bent frame) is <strong className="text-red-700">NOT covered</strong> under any circumstances.</li>
                <li><strong className="text-red-700">Liquid or water damage</strong> is <strong className="text-red-700">NOT covered</strong>, regardless of exposure or cause.</li>
                <li>Damage from misuse, drops, unauthorized repairs, or third-party tampering <strong className="text-red-700">voids this warranty</strong>.</li>
                <li>Battery, charger and accessories are <strong className="text-red-700">not covered</strong> — these are consumables.</li>
                <li>{SHOP.name} is <strong className="text-red-700">not liable</strong> for data loss, loss of use, business interruption or any indirect damages.</li>
                <li>Warranty is <strong className="text-red-700">non-transferable</strong> and requires this card plus proof of purchase to be honored.</li>
                <li>All warranty decisions rest at the sole discretion of {SHOP.name}.</li>
              </ul>
            </div>

            {/* Brands we sell */}
            <div className="bg-slate-100 border-t border-slate-300 px-8 py-5 flex items-center justify-center gap-10" data-testid="warranty-brands">
              {BRANDS.map((b) => (
                <div key={b.name} className="flex flex-col items-center gap-1.5">
                  {b.src ? (
                    <img src={b.src} alt={b.name} className="h-8 object-contain" />
                  ) : (
                    <span className="text-2xl font-black italic text-orange-500 tracking-tight">{b.wordmark}</span>
                  )}
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{b.label}</span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="bg-blue-900 text-blue-100 px-8 py-2.5 text-center text-[10px] uppercase tracking-[0.2em]">
              Thank you for shopping with {SHOP.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Warranty;
